import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { BookOpen, ExternalLink, Loader2, Sparkles, ChevronDown, FileText } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { GLOBAL_COUNTRIES as COUNTRIES } from '../family/shared';
import { LiteralText } from './LiteralText';

const TYPES = [['constitucion', 'Constitución'], ['codigo', 'Códigos'], ['ley', 'Leyes principales']];

const NormCard = ({ country, n, matterId }) => {
  const { api } = useAuth();
  const [open, setOpen] = useState(false);
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(false);

  const [cited, setCited] = useState({});
  const [lit, setLit] = useState(false);

  const cite = async (a, i) => {
    if (!matterId) return toast.error('Elige arriba el caso del CRM donde insertar la norma');
    try {
      const { data } = await api.post('/legislative/legal-order/cite', { matter_id: matterId, country, norm_id: n.id, articulo: a.articulo, contenido: a.contenido });
      setCited((c) => ({ ...c, [`${matterId}-${i}`]: true }));
      if (data.duplicate) toast.info(`${a.articulo} ya estaba guardado en este caso`);
      else toast.success(`${a.articulo} guardado en el caso: se citará en el próximo escrito`);
      if (data.warning) toast.warning(data.warning);
    } catch (err) { toast.error(err.response?.data?.detail || 'No se pudo insertar'); }
  };

  const toggle = async () => {
    if (loading) return;
    setOpen(!open);
    if (s || open) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/legislative/legal-order/${country}/${n.id}/summary`);
      setS(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo generar el resumen');
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid={`norm-${n.id}`} className="rounded-xl border border-slate-800 bg-slate-900/60 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-medium text-white">{n.name}</p>
        <div className="flex gap-2">
          <a href={n.url} target="_blank" rel="noreferrer" data-testid={`norm-link-${n.id}`} className="inline-flex items-center gap-1 rounded-full border border-slate-700 px-3 py-1 text-xs text-slate-300 hover:bg-slate-800">Texto vigente oficial <ExternalLink className="h-3 w-3" /></a>
          {n.has_text && (
            <button data-testid={`norm-literal-btn-${n.id}`} onClick={() => setLit(!lit)} className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${lit ? 'bg-emerald-500 text-slate-950' : 'border border-emerald-500/60 text-emerald-200 hover:bg-emerald-500/10'}`}>
              <FileText className="h-3 w-3" /> Texto literal{n.text ? ` · ${n.text.articles} arts.` : ''}
            </button>
          )}
          <button data-testid={`norm-summary-btn-${n.id}`} onClick={toggle} className="inline-flex items-center gap-1 rounded-full bg-blue-600 px-3 py-1 text-xs font-semibold text-white hover:bg-blue-500">
            {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />} Resumen IA <ChevronDown className={`h-3 w-3 transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </div>
      {lit && <div className="mt-3 border-t border-slate-800 pt-3"><LiteralText country={country} norm={n} onCite={cite} citedKey={(k) => !!cited[`${matterId}-${k}`]} /></div>}
      {open && s && (
        <div data-testid={`norm-summary-${n.id}`} className="mt-3 space-y-2 text-sm text-slate-300">
          <p>{s.que_regula}</p>
          {s.estructura?.length > 0 && <p className="text-xs text-slate-400">Estructura: {s.estructura.join(' · ')}</p>}
          <ul className="space-y-1">{(s.articulos_clave || []).map((a, i) => (
            <li key={i} className="grid grid-cols-[1fr_auto] items-start gap-3">
              <span><span className="font-semibold text-amber-300">{a.articulo}:</span> {a.contenido}</span>
              <button data-testid={`norm-cite-${n.id}-${i}`} onClick={() => cite(a, i)} className={`shrink-0 rounded-full border px-2 py-0.5 text-[11px] ${cited[`${matterId}-${i}`] ? 'border-emerald-500 bg-emerald-500/20 text-emerald-100' : 'border-emerald-500/50 text-emerald-200 hover:bg-emerald-500/10'}`}>{cited[`${matterId}-${i}`] ? '✓ Insertado' : '+ Insertar en escrito'}</button>
            </li>
          ))}</ul>
          {s.reformas_recientes?.length > 0 && <p className="text-xs"><span className="text-slate-400">Reformas recientes:</span> {s.reformas_recientes.join('; ')}</p>}
          <p className="text-xs text-indigo-200">{s.uso_practico}</p>
          <p className="text-[11px] text-slate-500">{s.vigencia}</p>
        </div>
      )}
    </div>
  );
};

export const LegalOrder = () => {
  const { api } = useAuth();
  const [country, setCountry] = useState('chile');
  const [data, setData] = useState(null);
  const [matters, setMatters] = useState([]);
  const [matterId, setMatterId] = useState('');

  useEffect(() => {
    api.get('/crm/matters', { params: { limit: 100 } }).then((r) => setMatters(r.data.matters || [])).catch(() => {});
  }, [api]);

  useEffect(() => {
    setData(null);
    api.get(`/legislative/legal-order/${country}`).then((r) => setData(r.data)).catch(() => toast.error('No se pudo cargar el ordenamiento'));
  }, [api, country]);

  return (
    <div className="space-y-6" data-testid="legal-order">
      <div className="flex flex-wrap gap-2">
        {COUNTRIES.map((c) => (
          <button key={c.code} data-testid={`order-country-${c.code}`} onClick={() => setCountry(c.code)}
            className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${country === c.code ? 'border-blue-400 bg-blue-600 text-white' : 'border-slate-700 text-slate-300 hover:bg-slate-800'}`}>
            {c.flag} {c.name}
          </button>
        ))}
      </div>
      {!data ? <Loader2 className="h-6 w-6 animate-spin text-blue-400" /> : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="flex items-center gap-2 text-sm text-slate-400"><BookOpen className="h-4 w-4 text-blue-400" /> Fuente oficial del texto vigente: {data.portal}</p>
            <select data-testid="order-matter-select" value={matterId} onChange={(e) => { setMatterId(e.target.value); const m = matters.find((x) => x.id === e.target.value); if (m && m.country && m.country !== country && COUNTRIES.some((c) => c.code === m.country)) { setCountry(m.country); toast.info('Mostrando la normativa del país del caso'); } }} className="rounded-lg border border-slate-700 bg-slate-950/60 px-3 py-1.5 text-sm text-white">
              <option value="">Caso para insertar artículos…</option>
              {matters.map((m) => <option key={m.id} value={m.id}>{COUNTRIES.find((c) => c.code === m.country)?.flag || ''} {m.title}</option>)}
            </select>
          </div>
          {TYPES.map(([t, label]) => {
            const items = data.norms.filter((n) => n.type === t);
            return items.length ? (
              <section key={t}>
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-amber-400">{label}</h3>
                <div className="grid gap-2">{items.map((n) => <NormCard key={`${country}-${n.id}`} country={country} n={n} matterId={matterId} />)}</div>
              </section>
            ) : null;
          })}
        </>
      )}
    </div>
  );
};
